import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { Dropdown, ListGroup } from 'react-bootstrap'
import { useToggle } from 'react-use'
import type {
  PivotLayout,
  PivotLayoutAggregatedMeasure,
  PivotLayoutCalculatedMeasure,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMeasure,
} from '@ztechapps/common.contract'
import type { PivotData } from '../pivot/types'
import type { PivotFieldDragPayload } from '../pivot/pivotFieldDrag.utils'
import { DirectionEnum } from '@ztechapps/common.contract'
import { LinkDropdownToggle } from '@ztechapps/webui.data-tools'
import { HorizontalStack, Icon, ShowIf, dragSortItemClassName, useDragSort } from '@ztechapps/webui.util'
import { PIVOT_ZONE_LABELS } from '../pivot/constants'
import { isPivotCalculatedMeasure } from '@ztechapps/common.utils'
import { PivotZoneEnum } from '../pivot/PivotZoneEnum'
import { findPivotFieldDragType, toPivotZoneDragType } from '../pivot/pivotFieldDrag.utils'
import {
  addPivotLayoutItem,
  findPivotLayoutItem,
  hasPivotFilterSelection,
  movePivotLayoutItem,
  prunePivotValueFilters,
  removePivotLayoutItem,
  setPivotFilterSetting,
  setPivotMeasureAggregation,
  setPivotMemberSort,
} from '../pivot/pivotLayout.utils'
import { getPivotZoneItems, setPivotZoneItems } from '../pivot/pivotZoneItems.utils'
import { toPivotFieldLabel } from '../pivot/toPivotFieldLabel'
import { toPivotMeasureLabel } from '../pivot/toPivotMeasureLabel'
import { PivotAggregationDropdown } from './PivotAggregationDropdown'
import { PivotCalculatedValueModal } from './PivotCalculatedValueModal'
import { PivotFilterModal } from './PivotFilterModal'

/** Props for a pivot layout drop zone. */
export type FieldZoneProps = {
  zone: PivotZoneEnum
  layout: PivotLayout
  timeZone: string
  data: PivotData
  onChange: (layout: PivotLayout) => void
}

const getFieldZoneItemKey = (item: PivotLayoutField | PivotLayoutMeasure): string =>
  `${item.field}|${'dateGroup' in item ? (item.dateGroup ?? '') : ''}|${'aggregation' in item ? item.aggregation : ''}`

/** Drag-sortable pivot layout zone with field settings. */
export const FieldZone = ({ zone, layout, timeZone, data, onChange }: FieldZoneProps) => {
  const fields = data.fields
  const items = getPivotZoneItems(layout, zone)
  const memberSorts = layout.memberSorts
  const fieldByName = new Map(fields.map((field) => [field.name, field]))
  // a value filter is edited from its Rows or Columns field, so an entry holding only one has no item in Report filters
  const isValueFilterOnly = (item: PivotLayoutField | PivotLayoutMeasure): boolean => {
    const entry = item as PivotLayoutFilter
    return (
      zone === PivotZoneEnum.Filters &&
      (entry.valueFilter !== undefined || entry.valueComparison !== undefined) &&
      !hasPivotFilterSelection(entry)
    )
  }
  // an item shows when it is a calculated value in Values or the report still returns its field
  const isVisibleZoneItem = (item: PivotLayoutField | PivotLayoutMeasure): boolean =>
    ((zone === PivotZoneEnum.Measures && isPivotCalculatedMeasure(item)) || fieldByName.has(item.field)) &&
    !isValueFilterOnly(item)
  const visibleItems =
    zone === PivotZoneEnum.Measures
      ? // a calculated value carries no aggregation, so the field test below would hide it — the formula test keeps it
        items.filter(
          (item): item is PivotLayoutAggregatedMeasure | PivotLayoutCalculatedMeasure =>
            isPivotCalculatedMeasure(item) || (fieldByName.has(item.field) && 'aggregation' in item)
        )
      : // other zones hold plain fields, so the shared visibility test applies directly
        items.filter(isVisibleZoneItem)

  const reorder = (source: (PivotLayoutField | PivotLayoutMeasure)[], oldIndex: number, newIndex: number) => {
    // the visibility test closes over this zone, so each zone reorders its own visible items
    const visible = source.filter(isVisibleZoneItem)
    const next = [...visible]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved)
    // items the report no longer returns stay hidden where they are instead of being dropped
    let cursor = 0
    return source.map((item) => (isVisibleZoneItem(item) ? next[cursor++] : item))
  }

  const { getItemProps, getHandleProps, getContainerProps } = useDragSort<PivotLayoutField | PivotLayoutMeasure>({
    items: visibleItems,
    getItemKey: getFieldZoneItemKey,
    // a reorder can make a ranked field the outermost of its axis, which a per parent value filter cannot survive
    onSort: ({ oldIndex, newIndex }) =>
      onChange(prunePivotValueFilters(setPivotZoneItems(layout, zone, reorder(items, oldIndex, newIndex)))),
  })

  // a same-zone drag carries this zone's own format, which the finder never returns — it stays with the hook
  const [dropActive, setDropActive] = useToggle(false)

  const otherZones = Object.values(PivotZoneEnum).filter((other) => other !== zone)

  return (
    <div>
      <div className="small fw-semibold">{PIVOT_ZONE_LABELS[zone]}</div>
      <ListGroup
        {...getContainerProps()}
        className={`overflow-y-auto${dropActive ? ' bg-primary-subtle' : ''}`}
        // the row and its menu toggles are `position-static`, so a menu is placed against the `.modal-body` (Bootstrap
        // `position: relative`, the nearest positioned ancestor) and this scroll box does not clip it; `position: fixed`
        // would be offset by the dialog's react-draggable transform
        // two zone rows plus the dialog chrome fit the dialog's viewport cap — a long zone scrolls in its own box
        style={{ minHeight: '6rem', maxHeight: '25vh' }}
        // the hook's item handlers stop bubbled drag events, so foreign drags are caught here on capture
        onDragOverCapture={(event) => {
          if (findPivotFieldDragType(event.dataTransfer.types, zone)) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            setDropActive(true)
          }
        }}
        onDragLeaveCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropActive(false)
        }}
        onDropCapture={(event) => {
          const dragType = findPivotFieldDragType(event.dataTransfer.types, zone)
          if (!dragType) return
          event.preventDefault()
          setDropActive(false)
          const payload = JSON.parse(event.dataTransfer.getData(dragType)) as PivotFieldDragPayload
          if (payload.zone !== undefined && payload.index !== undefined) {
            onChange(movePivotLayoutItem(layout, payload.zone, payload.index, zone, fields))
          } else {
            const field = fieldByName.get(payload.field)
            if (field) onChange(addPivotLayoutItem(layout, zone, field, payload.dateGroup))
          }
        }}
      >
        {visibleItems.map((item, visibleIndex) => {
          const index = items.indexOf(item)
          // a calculated value names no response field, so this stays undefined for one
          const pivotField = fieldByName.get(item.field)
          const calculatedMeasure = isPivotCalculatedMeasure(item) ? item : undefined
          const aggregatedMeasure = 'aggregation' in item ? item : undefined
          const measure = calculatedMeasure ?? aggregatedMeasure
          const isCalculated = calculatedMeasure !== undefined
          // a rename keeps its own name free, so the editor excludes only the other calculated values
          const takenNames = [
            ...fields.map((field) => field.name),
            ...layout.measures
              .filter(isPivotCalculatedMeasure)
              .filter((other) => other !== item)
              .map((other) => other.field),
          ]
          // measures carry no dateGroup — the label below only reads it for plain fields, so this stays undefined there
          const dateGroup = 'dateGroup' in item ? item.dateGroup : undefined
          const memberSortDirection = findPivotLayoutItem(memberSorts, item)?.direction
          const label =
            zone === PivotZoneEnum.Measures && measure
              ? toPivotMeasureLabel(measure)
              : toPivotFieldLabel(pivotField, item.field, dateGroup)
          return (
            <ListGroup.Item
              key={getFieldZoneItemKey(item)}
              className={`${dragSortItemClassName} position-static py-1 px-2 small`}
              {...getItemProps(visibleIndex)}
              {...getHandleProps(visibleIndex)}
              onDragStart={(event) => {
                getHandleProps(visibleIndex).onDragStart(event)
                // a calculated value reorders within Values only — with no payload no other zone accepts it
                if (isCalculated) return
                event.dataTransfer.setData(
                  toPivotZoneDragType(zone),
                  JSON.stringify({ field: item.field, dateGroup, zone, index })
                )
              }}
            >
              <HorizontalStack gap={2}>
                <div className="flex-grow-1">{label}</div>
                <ShowIf condition={zone === PivotZoneEnum.Filters}>
                  <PivotFilterModal
                    data={data}
                    field={item}
                    layout={layout}
                    timeZone={timeZone}
                    onApply={(setting) => onChange(setPivotFilterSetting(layout, item, setting))}
                  />
                </ShowIf>
                {zone === PivotZoneEnum.Measures && aggregatedMeasure ? (
                  <PivotAggregationDropdown
                    field={pivotField}
                    label={label}
                    aggregation={aggregatedMeasure.aggregation}
                    onSelect={(aggregation) => onChange(setPivotMeasureAggregation(layout, index, aggregation))}
                  />
                ) : null}
                <Dropdown className="position-static">
                  <Dropdown.Toggle
                    as={LinkDropdownToggle}
                    icon="ellipsis-vertical"
                    bsStyle="secondary"
                    aria-label={`${item.field} actions`}
                  />
                  <Dropdown.Menu>
                    <ShowIf condition={!isCalculated}>
                      {otherZones.map((other) => (
                        <Dropdown.Item
                          key={other}
                          onClick={() => onChange(movePivotLayoutItem(layout, zone, index, other, fields))}
                        >
                          Move to {PIVOT_ZONE_LABELS[other]}
                        </Dropdown.Item>
                      ))}
                    </ShowIf>
                    <ShowIf condition={zone === PivotZoneEnum.Rows || zone === PivotZoneEnum.Columns}>
                      <Dropdown.Item
                        active={memberSortDirection === DirectionEnum.asc}
                        onClick={() =>
                          onChange(
                            setPivotMemberSort(
                              layout,
                              item,
                              memberSortDirection === DirectionEnum.asc ? undefined : DirectionEnum.asc
                            )
                          )
                        }
                      >
                        Sort A to Z
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={memberSortDirection === DirectionEnum.desc}
                        onClick={() =>
                          onChange(
                            setPivotMemberSort(
                              layout,
                              item,
                              memberSortDirection === DirectionEnum.desc ? undefined : DirectionEnum.desc
                            )
                          )
                        }
                      >
                        Sort Z to A
                      </Dropdown.Item>
                      <PivotFilterModal
                        data={data}
                        field={item}
                        layout={layout}
                        timeZone={timeZone}
                        onApply={(setting) => onChange(setPivotFilterSetting(layout, item, setting))}
                        trigger={({ onClick }) => <Dropdown.Item onClick={onClick}>Filter…</Dropdown.Item>}
                      />
                    </ShowIf>
                    {calculatedMeasure ? (
                      <PivotCalculatedValueModal
                        trigger={({ onClick }) => <Dropdown.Item onClick={onClick}>Edit</Dropdown.Item>}
                        data={data}
                        measure={calculatedMeasure}
                        takenNames={takenNames}
                        onApply={(measure) =>
                          onChange({
                            ...layout,
                            measures: layout.measures.map((current, i) => (i === index ? measure : current)),
                          })
                        }
                      />
                    ) : null}
                    <Dropdown.Item onClick={() => onChange(removePivotLayoutItem(layout, zone, index))}>
                      Remove
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <span aria-hidden="true" className="text-secondary">
                  <Icon name="bars" />
                </span>
              </HorizontalStack>
            </ListGroup.Item>
          )
        })}
        <ShowIf condition={isEmpty(visibleItems)}>
          <ListGroup.Item className="py-1 px-2 small text-secondary fst-italic">Drop field here</ListGroup.Item>
        </ShowIf>
        <ShowIf condition={zone === PivotZoneEnum.Columns}>
          <ListGroup.Item className="py-1 px-2 small text-secondary">Σ Values</ListGroup.Item>
        </ShowIf>
      </ListGroup>
    </div>
  )
}
